import { Environment } from "@/src/types/digitalData";

import { sha256 } from "../cryptoUtils";
import { PRODUCT_ABBREVIATION } from "./constants";

/**
 * Generates a unique pageID for the page.
 * - For auction detail pages: extracts auction ID from URL (e.g., "/my-auctions/123" -> "123")
 * - For blog article pages: uses article slug from URL (e.g., "/blog/article-slug" -> "article-slug")
 * - For other pages: hashes the pathname to ensure consistency
 * Same pathname always generates the same pageID
 *
 * Note: Strips query parameters and hash fragments before processing to ensure consistent pageID values
 */
export const generatePageID = async (pathname: string): Promise<string> => {
  // Strip query parameters and hash fragments for consistent matching
  const cleanPathname = pathname.split("?")[0].split("#")[0];

  // Extract auction ID from consumer auction detail pages: /my-auctions/:auctionId
  const myAuctionsMatch = /^\/my-auctions\/(\d+)$/.exec(cleanPathname);
  if (myAuctionsMatch) {
    return myAuctionsMatch[1];
  }

  // Extract auction ID from dealer auction detail pages: /dealer/auction/:id
  const dealerAuctionMatch = /^\/dealer\/auction\/(\d+)$/.exec(cleanPathname);
  if (dealerAuctionMatch) {
    return dealerAuctionMatch[1];
  }

  // Extract article slug from blog post pages: /blog/:slug
  // Only match single-segment slugs (no nested paths)
  const blogPostMatch = /^\/blog\/([^/]+)$/.exec(cleanPathname);
  if (blogPostMatch) {
    // Use the article slug as pageID (it's already unique and consistent)
    return blogPostMatch[1];
  }

  // For all other pages, hash the clean pathname to ensure consistency
  return sha256(cleanPathname);
};

/**
 * Generates pageInstanceID in format: [pageID]_[product_abbreviation]_[environment]
 */
export const generatePageInstanceID = async (
  pageID: string,
  environment: Environment
): Promise<string> => {
  return `${pageID}_${PRODUCT_ABBREVIATION}_${environment}`;
};
